import { useEffect, useRef, useState } from 'react';

const defaultMinWidth = 15;
const defaultWidth = 150;
const defaultMaxWidth = 400;

function initSizes(columns:Array<any>) {
  const result:Array<any> = [];

  columns.forEach(c => {
    const width = !c.width || c.width < 0 ? defaultWidth : c.width;
    
    let minWidth = !c.minWidth || c.minWidth < 0 ? defaultMinWidth : c.minWidth;
    if (minWidth > width) {
      minWidth = width;
    }

    let maxWidth = !c.maxWidth || c.maxWidth < 0 ? defaultMaxWidth : c.maxWidth;
    if (maxWidth < width) {
      maxWidth = width;
    }

    result.push({
      id: c.accessor,
      resizable: c.resizable,
      width,
      minWidth,
      maxWidth,
    });
  });

  return result;
}

let originalX:number|null = null;
let originalWidth:number|null = null;
let originalAfterWidth:number|null = null;
let resizeIndex:number|null = null;
let originalFooterWidth:number|null = null;
let originalHeaderWidth:number|null = null;

function getHeaderElement(tableId:string, index:number) {
  return document.getElementById(`${tableId}-header-${index}`);
}

function resizeColumn(tableId:string, columnIndex:number, width:number, delta:number, resizeFooter:boolean, resizeHeader:boolean) {
  const headerElement = getHeaderElement(tableId, columnIndex);
  if (!headerElement) {
    return;
  }

  headerElement.style.width = width + 'px';

  let row = 0;
  let element = document.getElementById(`${tableId}-cell-${row}-${columnIndex}`);

  while (element) {
    element.style.width = width + 'px';
    row = row + 1;
    element = document.getElementById(`${tableId}-cell-${row}-${columnIndex}`);
  }

  // If there is footer - resize it - respect the colspan
  if (resizeFooter) {
    let footerElement = document.getElementById(`${tableId}-footer-${columnIndex}`);

    if (footerElement) {
      footerElement.style.width = ((originalFooterWidth as number) + delta) + 'px';
    }
  }

  // If there is header - resize it - respect the colspan
  if (resizeHeader) {
    let headerElement = document.getElementById(`${tableId}-custom-header-${columnIndex}`);

    if (headerElement) {
      headerElement.style.width = ((originalHeaderWidth as number) + delta) + 'px';
    }
  }
}

export default function useFlexResizer(tableId:string, columns:Array<any>, onColumnsResized:any) {
  const initColSizes = initSizes(columns);
  const [columnSizes, _setColumnSizes] = useState(initColSizes);

  const cRef = useRef(initColSizes);

  useEffect(() => {
    originalX = null;
    originalWidth = null;
    originalAfterWidth = null;
    resizeIndex = null;
    originalFooterWidth = null;
    originalHeaderWidth = null;

    setColumnSizes(initSizes(columns));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  function setColumnSizes(s:any) {
    cRef.current = s;
    _setColumnSizes(s);
  }

  function resize(e:MouseEvent) {
    const delta = e.pageX - (originalX as number);

    const newWidth = (originalWidth as number) + delta;
    const newAfterWidth = (originalAfterWidth as number) - delta;

    if (
      newWidth < cRef.current[resizeIndex as number].minWidth ||
      newWidth > cRef.current[resizeIndex as number].maxWidth ||
      newAfterWidth < cRef.current[(resizeIndex as number) + 1].minWidth ||
      newAfterWidth > cRef.current[(resizeIndex as number) + 1].maxWidth
    ) {
      return;
    }

    resizeColumn(tableId, (resizeIndex as number), newWidth, delta, true, true);
    resizeColumn(tableId, (resizeIndex as number) + 1, newAfterWidth, delta, false, false);
  }

  function stopResize() {
    window.removeEventListener('mousemove', resize);
    window.removeEventListener('mouseup', stopResize);

    const headerElement = getHeaderElement(tableId, resizeIndex as number);
    const headerAfterElement = getHeaderElement(tableId, (resizeIndex as number) + 1);
    if (!headerElement || !headerAfterElement) {
      return;
    }

    const finalWidth = parseFloat(getComputedStyle(headerElement).getPropertyValue('width').replace('px', ''));
    const finalAfterWidth = parseFloat(getComputedStyle(headerAfterElement).getPropertyValue('width').replace('px', ''));

    const newColSizes = [...columnSizes];
    newColSizes[resizeIndex as number].width = finalWidth;
    newColSizes[(resizeIndex as number) + 1].width = finalAfterWidth;

    const cbValues = [{
      id: newColSizes[resizeIndex as number].id,
      index: resizeIndex,
      width: finalWidth
    }, {
      id: newColSizes[(resizeIndex as number) + 1].id,
      index: (resizeIndex as number) + 1,
      width: finalAfterWidth
    }];

    originalX = null;
    originalWidth = null;
    originalAfterWidth = null;
    resizeIndex = null;
    originalFooterWidth = null;
    originalHeaderWidth = null;

    setColumnSizes(newColSizes);
    if (onColumnsResized) {
      onColumnsResized(cbValues);
    }
  }

  function onResizeStart(index:number) {
    // Implementation note:
    //   The last column cannot be resized directly to maintain flex layout (100%)
    // The resizer will check if the next column exists and also is it resizable.
    // If the next column is not resizable the selected column cannot resize as well.
    if (index < 0 || index >= columnSizes.length - 1) {
      return;
    }

    if (!cRef.current[index].resizable || !cRef.current[index + 1].resizable) {
      return;
    }

    const headerElement = getHeaderElement(tableId, index as number);
    const headerAfterElement = getHeaderElement(tableId, (index as number) + 1);
    if (!headerElement || !headerAfterElement) {
      return;
    }
    
    originalX = headerElement.getBoundingClientRect().right;

    originalWidth = parseFloat(getComputedStyle(headerElement).getPropertyValue('width').replace('px', ''));
    originalAfterWidth = parseFloat(getComputedStyle(headerAfterElement).getPropertyValue('width').replace('px', ''));

    let footerElement = document.getElementById(`${tableId}-footer-${index}`);
    if (footerElement) {
      originalFooterWidth = parseFloat(getComputedStyle(footerElement).getPropertyValue('width').replace('px', ''));
    }

    let customeHeaderElement = document.getElementById(`${tableId}-custom-header-${index}`);
    if (customeHeaderElement) {
      originalHeaderWidth = parseFloat(getComputedStyle(customeHeaderElement).getPropertyValue('width').replace('px', ''));
    }

    resizeIndex = index;

    window.addEventListener('mousemove', resize);
    window.addEventListener('mouseup', stopResize);
  }

  return [columnSizes, onResizeStart];
}